import React, { useCallback, useEffect, useState } from 'react';
import { SportsFavoritesMenuManifestazioneDto, SportsSottoVoceMenu, SportsVoceMenu } from 'types/swagger';
import { StaticMenu, seoItemHierarchy } from 'components/staticMap';
import { isMatch, isTruthy } from 'utility/functions';
import { matchesOrario, matchesQuote } from './utils';

import { DatoCmsSportNav } from 'lib/datoCms/types';
import { ElementToFilter } from '../sportNav/types';
import { SportNavAccordionList } from './sportNavAccordionList';
import { SportNavHighlightItem } from './sportNavHighlightItem';
import { SportNavTotocalcio } from './sportNavTotocalcio';
import classNames from 'classnames';
import { convertInThemeLogoType } from 'lib/datoCms/utils';
import { selectInfoTemplateManifestazione, selectIsInfoOpen, selectMenuManifestazioni } from 'features/sport/selectors';
import styles from './SportNav.module.scss';
import { useGetLabelsByKeys, useTranslation } from 'hooks/useLingUI';
import { useTypedSelector } from 'lib/centralStore';
import useWindowSize from 'hooks/useWindowSize';
import { Icon, IconsEnum } from 'components/Icons';
import { SportNavFilters } from './sportNavFilters';
import { SportResultsAndInfoContent } from '../sportResultsAndInfoContent';
import { StatisticType } from 'features/sport/utils/betRadar/statisticTypeEnum';
import { SportResultsAndInfoToggler } from '../sportResultsAndInfoToggler';
import { useRouter } from 'next/router';

export type SportNavProps = {
  gruppi: Array<SportsVoceMenu>;
  className?: string;
  disciplina?: string;
  staticMenu?: seoItemHierarchy;
  totocalcio?: DatoCmsSportNav['totocalcio'] | undefined;
  manifestazione?: string;
  manifestazioniPreferiti: Array<SportsFavoritesMenuManifestazioneDto>;
  manifestazioniInEvidenza?: DatoCmsSportNav['manifestazioniInEvidenza'];
};

export const SportNav = ({
  gruppi,
  className,
  totocalcio,
  disciplina,
  staticMenu,
  manifestazione,
  manifestazioniPreferiti,
  manifestazioniInEvidenza,
}: SportNavProps) => {
  const { t } = useTranslation();

  const [labelInEvidenza, labelGiocaTotocalcio, titleMobile, labelAllManifestazioni] = useGetLabelsByKeys([
    'in-evidenza',
    'gioca-totocalcio',
    'sport-header-sticky',
    'label-all-manifestazioni',
  ])!;

  const isInfoOpen = useTypedSelector(selectIsInfoOpen);
  const { isLoading } = useTypedSelector(selectMenuManifestazioni);
  const { templateManifestazione: template } = useTypedSelector(selectInfoTemplateManifestazione);

  const selectedFilters = useTypedSelector(({ sport }) => sport.selectedFilters);
  const { quota, orario: selectedOrario } = selectedFilters ?? {};
  const [selectedQuotaMin, selectedQuotaMax] = quota ?? {};

  const { isMobile } = useWindowSize();

  const [isMenuOpen, setMenuOpen] = useState(false);
  const [isFilterOpen, setFilterOpen] = useState(false);

  const { pathname, asPath, isReady } = useRouter();
  const isAvvenimentoPage = isMatch(pathname, 'avvenimento');

  useEffect(() => {
    setMenuOpen(false);
    setFilterOpen(false);
  }, [isMobile, asPath, isReady]);

  const matchesScommesseFilters = useCallback(
    (manifestazione: ElementToFilter) => {
      if (matchesOrario(manifestazione, selectedOrario)) {
        return matchesQuote(manifestazione, selectedQuotaMin, selectedQuotaMax);
      }
      return false;
    },
    [selectedOrario, selectedQuotaMax, selectedQuotaMin]
  );

  const filteredGruppi: Array<SportsVoceMenu> = (gruppi ?? []).map((gruppo) => {
    const manifestazioni: Array<SportsSottoVoceMenu> = (gruppo?.manifestazioni ?? []).filter(
      matchesScommesseFilters
    ) as Array<SportsSottoVoceMenu>;
    return {
      ...gruppo,
      manifestazioni,
    };
  });

  const filteredManifestazioniPreferiti: Array<SportsFavoritesMenuManifestazioneDto> = (
    manifestazioniPreferiti ?? []
  )?.filter(matchesScommesseFilters);

  const isModal = isMobile && isMenuOpen;

  const renderHead = () => {
    let objHeader = (
      <button
        type="button"
        className={classNames(styles.head, {
          [styles.hidden]: isFilterOpen || isAvvenimentoPage,
        })}
        onClick={() => setMenuOpen((st) => !st)}
      >
        <span className={classNames(styles.text)}>{titleMobile}</span>
        <Icon
          key={`icon-${isModal}`}
          iconId={isModal ? IconsEnum.CLOSE : IconsEnum.CHEWRON_DOWN}
          color="var(--color-on-bg-secondary)"
        />
      </button>
    );

    if (manifestazione && template && isMobile && !isMenuOpen) {
      const { gruppo, gruppoTrKey, externalProvider, manifestazione, manifestazioneTrKey } = template ?? {};

      objHeader = (
        <div className={styles.headWrapper}>
          <button
            type="button"
            className={classNames(styles.head, {
              // [styles.modal]: isModal,
              [styles.hidden]: isFilterOpen,
            })}
            onClick={() => setMenuOpen((st) => !st)}
          >
            <ul>
              <li className={styles.gruppo}>{t(gruppoTrKey, gruppo)}</li>
              <li className={styles.manifestazione}>{t(manifestazioneTrKey, manifestazione)}</li>
            </ul>
            <span className={styles.changeSelection}>{t('cambia')}</span>
          </button>
          <SportResultsAndInfoToggler providerID={externalProvider} />
        </div>
      );
    }

    return (
      <React.Fragment>
        {objHeader}
        <button
          type="button"
          className={classNames(styles.filters, {
            [styles.modal]: isModal,
            [styles.hidden]: isFilterOpen,
          })}
          disabled={!disciplina}
          onClick={() => setFilterOpen((st) => !st)}
        >
          <span className={classNames(styles.text)}>{labelAllManifestazioni}</span>
          <Icon
            key={`icon-${isModal}-${isFilterOpen}`}
            iconId={isModal && isFilterOpen ? IconsEnum.CLOSE : IconsEnum.FILTER}
            color="var(--color-on-bg-secondary)"
          />
        </button>
      </React.Fragment>
    );
  };

  const renderContent = () => {
    if (isFilterOpen) {
      return (
        <SportNavFilters
          key={JSON.stringify(selectedFilters ?? {})}
          visibility={true}
          handleClose={setFilterOpen}
          className={classNames(styles.sidebar, className ?? '', { [styles.modal]: isModal })}
        />
      );
    }

    const hasTotoCalcio = totocalcio && isMatch(disciplina, '^CALCIO$');

    return (
      <React.Fragment>
        {!isModal && isMobile && isInfoOpen && (
          <SportResultsAndInfoContent
            isOpen
            className={styles.mobileOnly}
            providerID={+template?.externalProvider!}
            type={StatisticType.Manifestazione}
          />
        )}
        <aside
          className={classNames(styles.sidebar, className ?? '', {
            [styles.modal]: isModal,
            [styles.margin]: hasTotoCalcio,
            [styles.visibile]: isMenuOpen,
          })}
        >
          {isTruthy(manifestazioniInEvidenza?.length) && (
            <React.Fragment>
              <span className={styles.highlightedTitle}>{labelInEvidenza}</span>
              {manifestazioniInEvidenza?.map((manifestazioneInEvidenza) => {
                const { id, descrizione, link, gruppoId } = manifestazioneInEvidenza;
                const { href } = link[0];
                return <SportNavHighlightItem key={id} descrizione={descrizione} gruppoId={gruppoId} href={href} />;
              })}
            </React.Fragment>
          )}
          <div
            className={classNames(styles.wrapper, {
              [styles.withHighlighted]: isTruthy(manifestazioniInEvidenza?.length),
            })}
          >
            {disciplina ? (
              <SportNavAccordionList
                key={`Menu-${filteredGruppi.map((x) => x.id).join('-')}-${isLoading ? 'l' : 'r'}`}
                gruppi={filteredGruppi}
                isLoading={isLoading}
                manifestazioniPreferitiList={filteredManifestazioniPreferiti}
              />
            ) : (
              <StaticMenu key={`Menu-${isLoading ? 'l' : 'r'}`} item={staticMenu} onMountedDestroy={false} />
            )}
          </div>
        </aside>
        {hasTotoCalcio && (
          <SportNavTotocalcio
            className={classNames(className, styles.totoCalcio, { [styles.hidden]: isModal })}
            linkLabel={labelGiocaTotocalcio}
            image={convertInThemeLogoType(totocalcio?.logoTotocalcio[0]!)}
          />
        )}
      </React.Fragment>
    );
  };

  return (
    <div
      className={classNames(styles.container, styles[`${process.env.NEXT_PUBLIC_COUNTRY}`], {
        [styles.modal]: isModal,
      })}
    >
      {renderHead()}
      {renderContent()}
    </div>
  );
};
